/**
 * Connect flow WAHA — form de phone + WhatsappConnect legado para QR Code.
 * Espelha o que PublishChannelModal fazia nos steps WA_NEW/WA_GENERATING/WA_CONNECTING,
 * mas inline na detail view (não modal-in-modal).
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import NumberFormat from 'react-number-format';
import {
  IconBrandWhatsapp,
  IconPhone,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import {
  ConnectWhatsappAccount,
  CheckAccountStatus,
} from '../../../../../services/WaBottyServices';
import AgentsFlowService from '../../../services/AgentsFlowService';
import { formatPhoneForAPI } from '../../../../../helpers/Helpers';

// Lazy import do WhatsappConnect legado
const WhatsappConnect = React.lazy(() =>
  import('../../../../../jsx/components/WhatsAppChat/WhatsappConnect'),
);

const STEPS = {
  PHONE: 'phone',
  GENERATING: 'generating',
  CONNECTING: 'connecting',
  SUCCESS: 'success',
  ERROR: 'error',
};

const formatPhoneMask = (value) => {
  const clean = value.replace(/\D/g, '');
  if (clean.length === 0) return '';
  if (clean.length <= 2) return `(${clean}`;
  if (clean.length <= 6) return clean.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  if (clean.length <= 10) return clean.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return clean.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

const WahaConnectFlow = ({ agentId, onConnected, onCancel }) => {
  const [step, setStep] = useState(STEPS.PHONE);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const connectOrReconnectRef = useRef('connect');
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const cleanPhone = phoneNumber.replace(/\D/g, '');
  const isPhoneValid = cleanPhone.length === 10 || cleanPhone.length === 11;

  const handleGenerate = async () => {
    if (!isPhoneValid) {
      Swal.fire({
        icon: 'warning',
        title: 'Número inválido',
        text: 'Insira um número válido (10 ou 11 dígitos).',
      });
      return;
    }

    setStep(STEPS.GENERATING);
    try {
      const response = await ConnectWhatsappAccount({
        phone: cleanPhone,
        agent_flow_id: agentId || null,
      });

      if (response?.status === 'STARTING') {
        // Poll a cada 3s
        intervalRef.current = setInterval(async () => {
          try {
            const result = await CheckAccountStatus(cleanPhone, null);
            const status = result?.status || result?.data?.status;
            if (status === 'WORKING' || status === 'SCAN_QR_CODE') {
              cleanup();
              connectOrReconnectRef.current = 'connect';
              setStep(STEPS.CONNECTING);
            }
          } catch (err) {
            console.error('Erro ao verificar status:', err);
          }
        }, 3000);

        // Timeout 2min
        timeoutRef.current = setTimeout(() => {
          cleanup();
          setErrorMsg('Sessão não inicializou em 2 minutos. Tente novamente.');
          setStep(STEPS.ERROR);
        }, 120000);
      } else {
        setErrorMsg('Resposta inesperada ao iniciar sessão.');
        setStep(STEPS.ERROR);
      }
    } catch (err) {
      const errData = err?.response?.data || err?.data;
      if (errData?.requires_payment) {
        setErrorMsg(
          `Limite de conexões atingido (${errData.current_usage}/${errData.total_limit}). ${errData.error || ''}`,
        );
        setStep(STEPS.ERROR);
      } else if (err?.status === 422 || err?.response?.status === 422) {
        // Sessão já existe — vai direto para QR
        connectOrReconnectRef.current = 'connect';
        setStep(STEPS.CONNECTING);
      } else {
        setErrorMsg(errData?.error || 'Erro ao conectar. Tente novamente.');
        setStep(STEPS.ERROR);
      }
    }
  };

  const handleConnectionSuccess = useCallback(
    async (data) => {
      const formattedPhone =
        data?.phone || formatPhoneForAPI(phoneNumber).replace('+', '');

      try {
        await AgentsFlowService.updateAgent(agentId, {
          wabotty_account: formattedPhone,
        });
      } catch (err) {
        console.error('Erro ao atualizar wabotty_account:', err);
      }

      setStep(STEPS.SUCCESS);
      if (onConnected) onConnected({ phone_number: formattedPhone });
    },
    [agentId, phoneNumber, onConnected],
  );

  const handleConnectionFailure = useCallback(() => {
    setErrorMsg('Não foi possível conectar ao WhatsApp.');
    setStep(STEPS.ERROR);
  }, []);

  return (
    <div className="cloudapi-connect">
      {step === STEPS.PHONE && (
        <div style={{ padding: 16 }}>
          <div className="cloudapi-connect__intro-icon" style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
            <IconBrandWhatsapp size={32} />
          </div>
          <h4 style={{ textAlign: 'center', marginBottom: 8 }}>Conectar WhatsApp Não Oficial</h4>
          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, marginBottom: 24 }}>
            Conexão via QR Code. Funciona com qualquer número de WhatsApp comum.
          </p>

          <div className="cloudapi-field">
            <label className="cloudapi-field__label">
              <IconPhone size={12} style={{ marginRight: 4 }} />
              Número do WhatsApp
            </label>
            <NumberFormat
              className="cloudapi-field__input"
              format={formatPhoneMask}
              mask="_"
              placeholder="(00) 00000-0000"
              value={phoneNumber}
              onValueChange={(values) => setPhoneNumber(values.formattedValue)}
            />
            <p className="cloudapi-field__hint">
              Aceita telefones fixos (10 dígitos) e celulares (11 dígitos).
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
            <button type="button" onClick={onCancel} className="cloudapi-hero__action-btn">
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!isPhoneValid}
              style={{
                padding: '10px 20px',
                background: isPhoneValid ? '#25D366' : '#9ca3af',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: isPhoneValid ? 'pointer' : 'not-allowed',
              }}
            >
              Gerar QR Code
            </button>
          </div>
        </div>
      )}

      {step === STEPS.GENERATING && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spinner animation="border" variant="success" />
          <p style={{ marginTop: 16, fontSize: 14 }}>Iniciando sessão WhatsApp...</p>
          <p style={{ fontSize: 12, color: '#9ca3af' }}>Pode levar até 2 minutos.</p>
        </div>
      )}

      {step === STEPS.CONNECTING && (
        <React.Suspense
          fallback={
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Spinner animation="border" />
            </div>
          }
        >
          <WhatsappConnect
            phoneNumber={cleanPhone}
            agentId={null}
            connectOrReconnect={connectOrReconnectRef}
            onConnectionSuccess={handleConnectionSuccess}
            onConnectionFailure={handleConnectionFailure}
          />
        </React.Suspense>
      )}

      {step === STEPS.SUCCESS && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#dcfce7',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a',
            }}
          >
            <IconCheck size={32} />
          </div>
          <h4 style={{ marginBottom: 8 }}>WhatsApp conectado!</h4>
          <p style={{ color: '#6b7280', fontSize: 13 }}>{cleanPhone}</p>
        </div>
      )}

      {step === STEPS.ERROR && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#fef2f2',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
            }}
          >
            <IconAlertTriangle size={32} />
          </div>
          <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 13 }}>{errorMsg}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button type="button" onClick={onCancel} className="cloudapi-hero__action-btn">
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                setErrorMsg('');
                setStep(STEPS.PHONE);
              }}
              style={{
                padding: '10px 20px',
                background: '#25D366',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WahaConnectFlow;
